import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { myReferrals } from '../services/referralService';

// Parrainage direct : seule l'inscription vérifiée d'un filleul compte.
export default function ReferralPanel() {
  const { currentUser } = useUser();
  const userId = currentUser?.id;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    if (!userId) { setData(null); return undefined; }
    setData(null);
    setError('');
    myReferrals().then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.message || 'Parrainage indisponible'); });
    return () => { active = false; };
  }, [userId, retryKey]);

  const link = useMemo(() => {
    if (!data?.code) return '';
    const publicUrl = process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin;
    try {
      const url = new URL(publicUrl);
      url.search = '';
      url.hash = '';
      url.searchParams.set('ref', data.code);
      return url.href;
    } catch { return ''; }
  }, [data?.code]);

  async function copy(text) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setNotice('Copié dans le presse-papiers.');
    } catch {
      setNotice('Sélectionne le texte ci-dessous pour le copier.');
    }
  }

  async function invite() {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Parrainage ConcoursBF',
          text: 'Inscris-toi à ConcoursBF avec mon code ' + data.code,
          url: link,
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    await copy(link);
  }

  if (!currentUser) return null;

  return (
    <section id="profile-referrals" className="surface-card rounded-2xl shadow-sm border border-color p-5 sm:p-6"
      aria-label="Mon parrainage">
      <div className="flex flex-wrap gap-2 justify-between items-center mb-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-secondary">FAIRE DÉCOUVRIR CONCOURSBf</p>
          <h2 className="font-bold text-xl text-primary">Mon parrainage</h2>
        </div>
        {data?.program && <span className="text-xs text-secondary">
          {data.program.enabled ? 'Programme actif' : 'Programme temporairement désactivé'}
        </span>}
      </div>
      {error && <div role="alert" className="text-red-600 text-sm">{error}
        <button type="button" className="ml-2 underline font-semibold"
          onClick={() => setRetryKey(key => key + 1)}>Réessayer</button>
      </div>}
      {!data && !error && <p className="text-secondary text-sm">Chargement des invitations…</p>}
      {data && (
        <>
          <p className="text-sm text-secondary">
            Invite directement des proches. Seuls les nouveaux comptes ayant confirmé leur adresse e-mail comptent.
            Aucun recrutement indirect n'est pris en compte.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <label className="text-sm font-semibold">
              Mon code personnel
              <div className="flex gap-2 mt-1">
                <input aria-label="Code de parrainage" className="border border-color rounded-lg px-3 py-2 min-w-0 flex-1 text-primary bg-secondary"
                  readOnly value={data.code} onFocus={(e) => e.target.select()} />
                <button type="button" className="bg-blue-600 text-white rounded-lg px-3" onClick={() => copy(data.code)}>Copier</button>
              </div>
            </label>
            <label className="text-sm font-semibold">
              Mon lien d'invitation
              <div className="flex gap-2 mt-1">
                <input aria-label="Lien de parrainage" className="border border-color rounded-lg px-3 py-2 min-w-0 flex-1 text-primary bg-secondary"
                  readOnly value={link} onFocus={(e) => e.target.select()} />
                <button type="button" className="bg-blue-600 text-white rounded-lg px-3" onClick={invite}>Inviter</button>
              </div>
            </label>
          </div>
          <div className="mt-4 rounded-lg bg-secondary p-3">
            <div className="flex items-center justify-between gap-2 text-sm">
              <strong>{data.verified} inscription(s) validée(s)</strong>
              <span className="text-secondary">{data.invited} inscription(s) parrainée(s) au total</span>
            </div>
            <div className="mt-2 text-sm text-secondary">
              {data.granted} récompense(s) obtenue(s)
              {data.program.enabled && ' · ' + data.program.requiredSignups + ' filleuls par récompense · ' + data.program.rewardDays + ' jours'}
            </div>
            {data.program.enabled && (
              <div className="mt-3" aria-label="Progression des parrainages">
                <progress className="w-full" max={data.program.requiredSignups} value={data.progress} />
                <p className="text-xs text-secondary">
                  {data.program.repeatable || data.granted === 0
                    ? data.progress + ' / ' + data.program.requiredSignups + ' vers le prochain palier'
                    : 'Récompense unique déjà obtenue'}
                </p>
              </div>
            )}
          </div>
          {Array.isArray(data.rewards) && data.rewards.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-bold">Mes récompenses</h3>
              <ul className="text-sm mt-2 space-y-2">
                {data.rewards.map((reward) => (
                  <li key={reward.milestone} className="border border-color rounded-lg p-3">
                    <strong>Récompense n°{reward.milestone}</strong> — {reward.days} jours,
                    {reward.scope === 'all' ? ' tous les contenus Premium' : ' contenus Premium sélectionnés'}
                    <p className="text-secondary text-xs">
                      Du {new Date(reward.startsAt).toLocaleDateString('fr-FR')} au {new Date(reward.endsAt).toLocaleDateString('fr-FR')}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {notice && <p role="status" className="text-sm text-green-700 mt-2">{notice}</p>}
        </>
      )}
    </section>
  );
}
